/**
 * PublishSettings - Admin page for managing publish workflow
 *
 * Shows full publish status, pending changes, and allows
 * manual triggering of publish/rebuild.
 */
import {
  Upload, Clock, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react';

import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import usePublishStatus from '../../hooks/usePublishStatus';

const formatDate = (date) => {
  if (!date) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

const PublishSettings = () => {
  const {
    hasPendingChanges,
    lastChangeAt,
    scheduledPublishAt,
    lastPublishedAt,
    pendingChanges,
    autoPublishDelayMinutes,
    loading,
    publishing,
    refresh,
    triggerPublish,
    cancelPublish,
  } = usePublishStatus();

  const handlePublish = async () => {
    try {
      await triggerPublish();
    } catch (err) {
      console.error('Publish failed:', err);
    }
  };

  const handleCancel = async () => {
    try {
      await cancelPublish();
    } catch (err) {
      console.error('Cancel failed:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-6 w-6 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Status */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {hasPendingChanges ? (
              <>
                <AlertCircle className="h-5 w-5 text-amber-600" />
                Changes Pending
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 text-green-600" />
                All Published
              </>
            )}
          </CardTitle>
          <CardDescription>
            {hasPendingChanges
              ? `${pendingChanges.length} change${pendingChanges.length !== 1 ? 's' : ''} waiting to be published`
              : 'Your public site is up to date'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-stone-50 dark:bg-stone-800 rounded-lg p-4">
              <div className="text-sm text-stone-600 dark:text-stone-400 mb-1">Last Published</div>
              <div className="font-medium">{formatDate(lastPublishedAt)}</div>
            </div>
            <div className="bg-stone-50 dark:bg-stone-800 rounded-lg p-4">
              <div className="text-sm text-stone-600 dark:text-stone-400 mb-1">Last Change</div>
              <div className="font-medium">{formatDate(lastChangeAt)}</div>
            </div>
            {scheduledPublishAt && (
              <div className="bg-amber-50 dark:bg-amber-900/50 rounded-lg p-4 md:col-span-2">
                <div className="text-sm text-amber-700 dark:text-amber-300 mb-1 flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Scheduled Auto-Publish
                </div>
                <div className="font-medium text-amber-900 dark:text-amber-100">{formatDate(scheduledPublishAt)}</div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pending Changes */}
      {hasPendingChanges && pendingChanges.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Changes</CardTitle>
            <CardDescription>
              These changes will be published to your public site
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {pendingChanges.map((change, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <span className="w-2 h-2 bg-amber-500 rounded-full flex-shrink-0" />
                  {change}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Publish Actions</CardTitle>
          <CardDescription>
            Manually trigger or manage the publish process
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            <Button
              onClick={handlePublish}
              disabled={publishing || !hasPendingChanges}
              variant="adminGold"
            >
              <Upload className="h-4 w-4 mr-2" />
              {publishing ? 'Publishing...' : 'Publish Now'}
            </Button>

            {scheduledPublishAt && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={publishing}
              >
                Cancel Scheduled Publish
              </Button>
            )}

            <Button
              variant="ghost"
              onClick={refresh}
              disabled={publishing}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh Status
            </Button>
          </div>

          <p className="text-sm text-stone-500 dark:text-stone-400 mt-4">
            Content changes are automatically published {autoPublishDelayMinutes} minutes
            after the last change, or you can publish immediately using the button above.
          </p>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle>How Publishing Works</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-stone-600 dark:text-stone-400 space-y-2">
          <p>
            When you make changes in the admin panel, they are saved to the database
            immediately but not yet visible on the public website.
          </p>
          <p>
            After {autoPublishDelayMinutes} minutes of no changes, the system automatically
            rebuilds and deploys your public site with all pending updates.
          </p>
          <p>
            You can also click "Publish Now" to trigger an immediate deployment, or
            "Cancel" to stop a scheduled publish (the changes will remain pending).
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default PublishSettings;
